(() => {
  if (customElements.get('ch-guided-gallery')) return;
  class GuidedGallery extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.gallery = this.closest('media-gallery');
      if (!this.gallery) return;
      this.buttons = [...this.querySelectorAll('[data-ch-target]')];
      this.allButton = this.querySelector('.ch-gallery-all');
      this.cta = this.gallery.querySelector('[data-ch-video]');
      this.expanded = false;
      this.inView = true;
      const signal = this.controller.signal;
      customElements.whenDefined('media-gallery').then(() => {
        if (signal.aborted) return;
        this.start(0);
      });
    }
    start(attempt) {
      if (!this.isConnected || this.controller.signal.aborted) return;
      if (!this.gallery.sliderInstance?.slider) {
        if (attempt < 40) this.timer = setTimeout(() => this.start(attempt + 1), 100);
        return; // The original gallery remains usable if theme initialization fails.
      }
      const signal = this.controller.signal;
      this.hidden = false;
      this.gallery.setAttribute('data-ch-guided-ready', '');
      this.buttons.forEach(button => button.addEventListener('click', () => this.go(button.dataset.chTarget, button.hasAttribute('data-ch-play')), { signal }));
      this.cta?.addEventListener('click', () => this.go(this.cta.dataset.chVideo, true), { signal });
      this.allButton.addEventListener('click', () => {
        this.expanded = !this.expanded;
        this.allButton.setAttribute('aria-expanded', String(this.expanded));
        this.gallery.toggleAttribute('data-ch-all-images', this.expanded);
        this.gallery.thumbsInstance?.slider?.update();
      }, { signal });
      this.gallery.querySelectorAll('.product__thumbs-item').forEach((thumb, i) => {
        thumb.setAttribute('role', 'button');
        thumb.setAttribute('tabindex', '0');
        thumb.setAttribute('aria-label', thumb.querySelector('img')?.alt || String(i + 1));
        thumb.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            const target = this.media(thumb.dataset.target);
            this.go(thumb.dataset.target, target?.dataset.mediaType === 'video');
          }
        }, { signal });
      });
      this.observer = new MutationObserver(() => this.queueSync());
      this.observer.observe(this.gallery.querySelector('[id^="Slider-Gallery"]'), { subtree: true, attributes: true, attributeFilter: ['class'], childList: true });
      this.visibility = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (!this.inView) { this.pendingPlay = null; this.pauseExcept(); }
      });
      this.visibility.observe(this.gallery.querySelector('[id^="GalleryViewer"]'));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) { this.pendingPlay = null; this.pauseExcept(); }
      }, { signal });
      this.gallery.addEventListener('video:play', event => {
        const slide = event.target.closest('.product__media-item');
        if (!this.inView || document.hidden || !slide?.classList.contains('swiper-slide-active')) this.pauseExcept();
      }, { signal });
      this.sync();
    }
    media(id) {
      return [...this.gallery.querySelectorAll('.product__media-item')].find(slide => slide.dataset.mediaId === String(id));
    }
    go(id, play) {
      const media = this.media(id), slider = this.gallery.sliderInstance?.slider;
      if (!media || !slider || slider.destroyed) return;
      this.pendingPlay = play ? String(id) : null;
      this.pauseExcept();
      slider.slideToLoop(Number(media.dataset.mediaIndex), matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280);
      this.queueSync();
    }
    pauseExcept(activeId) {
      this.gallery.querySelectorAll('.product__media-item').forEach(slide => {
        if (slide.dataset.mediaId === activeId && slide.classList.contains('swiper-slide-active')) return;
        slide.querySelectorAll('video').forEach(video => video.pause());
        slide.querySelectorAll('video-element[playing]').forEach(player => player.pause?.());
      });
    }
    queueSync() {
      if (this.syncQueued) return;
      this.syncQueued = true;
      queueMicrotask(() => {
        this.syncQueued = false;
        if (this.isConnected && !this.controller.signal.aborted) this.sync();
      });
    }
    sync() {
      const active = this.gallery.querySelector('.product__media-item.swiper-slide-active');
      if (!active) return;
      const id = active.dataset.mediaId;
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.chTarget === id)));
      if (this.cta) this.cta.hidden = active.dataset.mediaType !== 'image';
      this.pauseExcept(id);
      if (this.pendingPlay === id && this.inView && !document.hidden) {
        this.pendingPlay = null;
        active.querySelector('video-element')?.play?.();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.visibility?.disconnect();
      clearTimeout(this.timer);
      this.pauseExcept();
      this.gallery?.removeAttribute('data-ch-guided-ready');
    }
  }
  customElements.define('ch-guided-gallery', GuidedGallery);
})();
